import {
  BellRing, Check, ChevronDown, ClipboardCheck, Coffee, GraduationCap, Headphones, Headset, LogOut,
  MicOff, Phone, PhoneCall, PhoneOff, PhoneOutgoing, UserCheck, Users, Utensils, WifiOff,
  type LucideIcon,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout, Spinner } from "@/components/ui/feedback";
import { useToast } from "@/components/ui/toast";
import {
  availability, BREAK_REASONS, lineSwitches, linesText, type AgentStep, type Availability,
  type AvailabilityTone, type LineSwitch,
} from "@/lib/acd";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { noCallsLine } from "@/lib/no-calls";
import { useAcdMe, useAgentAction, useDialerOffer } from "@/lib/queries";
import type { AgentState } from "@/lib/types";
import { cn } from "@/lib/utils";
import { openAudioSetup, useMicPermission } from "@/softphone/audio-setup";
import type { MicPermission } from "@/softphone/audio-devices";
import { useSoftphone, type RegistrationState } from "@/softphone/softphone-context";
import { seesCampaigns } from "@/lib/types";

/** The dot beside a state, in the design system's tones. The live floor uses the same ones. */
export const AVAILABILITY_DOT: Record<AvailabilityTone, string> = {
  success: "bg-success-9",
  warning: "bg-warning-9",
  accent: "bg-accent-9",
  neutral: "bg-gray-7",
};

/** The Phone page's larger trigger, filled in the state's tone so it reads from across the page. */
const AVAILABILITY_FILL: Record<AvailabilityTone, string> = {
  success: "border-success-9 bg-success-2 text-success-11 hover:bg-success-4",
  warning: "border-warning-9 bg-warning-2 text-warning-11 hover:bg-warning-4",
  accent: "border-accent-7 bg-accent-3 text-accent-11 hover:bg-accent-4",
  neutral: "border-gray-7 bg-surface text-text hover:bg-surface-hover",
};

/** A state's word on the rail. */
export const AVAILABILITY_TEXT: Record<AvailabilityTone, string> = {
  success: "text-success-11",
  warning: "text-warning-11",
  accent: "text-accent-11",
  neutral: "text-text-muted",
};

/** The band at the head of the menu, tinted in the state's tone. */
const AVAILABILITY_BAND: Record<AvailabilityTone, string> = {
  success: "bg-success-2",
  warning: "bg-warning-2",
  accent: "bg-accent-2",
  neutral: "bg-surface-sunken",
};

/** A disc that holds an icon, in a tone: the state's in the band, a choice's on its row. */
const DISC: Record<AvailabilityTone | "danger", string> = {
  success: "bg-success-4 text-success-11",
  warning: "bg-warning-4 text-warning-11",
  accent: "bg-accent-4 text-accent-11",
  neutral: "bg-gray-5 text-gray-11",
  danger: "bg-danger-2 text-danger-11",
};

/** The row of the choice the rep is on, tinted so it is found without reading. */
const CURRENT_ROW: Record<"success" | "accent", string> = {
  success: "bg-success-2 data-[highlighted]:bg-success-2",
  accent: "bg-accent-2 data-[highlighted]:bg-accent-2",
};

/** A state's picture in the menu's band. One the console does not know gets the phone's. */
const STATE_ICON: Record<string, LucideIcon> = {
  available: Headset,
  ringing: BellRing,
  busy: PhoneCall,
  busy_external: PhoneCall,
  dialing: PhoneOutgoing,
  disposition: ClipboardCheck,
  wrapup: ClipboardCheck,
  paused: Coffee,
  offline: WifiOff,
};

/** A break reason's picture, by its code (`BREAK_REASONS`). */
const BREAK_ICON: Record<string, LucideIcon> = {
  break: Coffee,
  lunch: Utensils,
  meeting: Users,
  training: GraduationCap,
};

/** An item of the menu draws its icons at the size and in the colour of the disc they are in,
 *  not the menu's own small grey ones. */
const ITEM_ICONS = "[&_svg]:size-4 [&_svg]:text-current";

/**
 * What the microphone says about taking calls, or null once it works (ACL-553). The phone's own
 * state is checked before it (`takeCallsBlock`).
 *
 * A rep who pressed Not now at sign-in, or whose browser blocks the microphone, could still go
 * Ready, and every call they took was silence to the lead. So Ready, My calls only and turning a
 * campaign on wait for the microphone; a break and signing out never do, since neither puts a call
 * through to them. Not read yet counts as not allowed for the moment it takes, so a press in that
 * moment cannot slip past the rule.
 */
export function micBlockedReason(permission: MicPermission | null): string | null {
  if (permission === "granted") return null;
  if (permission === "denied") {
    return "This browser is blocking your microphone. Allow it before you take calls.";
  }
  if (permission === "unavailable") {
    return "This browser cannot use a microphone here, so you cannot take calls.";
  }
  return "Allow your microphone before you take calls.";
}

/**
 * Why the rep may not start taking calls right now, and what fixes it (ACL-553): their phone first,
 * since no call reaches a phone that is not connected in this tab, then their microphone. Null once
 * both work. The same rows are held back for both, and only those (`micBlockedReason`).
 *
 * Connected means this tab holds the line. A phone on another tab is the rep's to move here with
 * Use the phone here, as the phone itself offers; going Ready from a tab that cannot ring would
 * send them calls they never see.
 */
export interface CallsBlock {
  text: string;
  /** What a row it holds back says instead of what it would do. */
  short: string;
  /** The one thing that fixes it from the menu, if anything does. */
  fix: "connect" | "take_over" | "mic" | null;
}

export function takeCallsBlock(
  registration: RegistrationState,
  permission: MicPermission | null,
): CallsBlock | null {
  if (registration === "connecting") {
    return {
      text: "Your phone is connecting. You can take calls once it is connected.",
      short: "Wait for your phone to connect",
      fix: null,
    };
  }
  if (registration === "standby") {
    return {
      text: "Your phone is in another browser tab. Take calls there, or use the phone here.",
      short: "Use the phone in this tab first",
      fix: "take_over",
    };
  }
  if (registration !== "registered") {
    return {
      text: "Your phone is not connected. Connect it before you take calls.",
      short: "Connect your phone first",
      fix: "connect",
    };
  }
  const mic = micBlockedReason(permission);
  return mic ? { text: mic, short: "Allow your microphone first", fix: "mic" } : null;
}

/** `takeCallsBlock` for this phone and this browser's microphone: every way back to Ready asks it,
 *  the menu and the Calling back break's way out (`call-back.tsx`). */
export function useTakeCallsBlock(): CallsBlock | null {
  const phone = useSoftphone();
  return takeCallsBlock(phone.registration, useMicPermission());
}

const FIX_LABEL: Record<NonNullable<CallsBlock["fix"]>, string> = {
  connect: "Connect your phone",
  take_over: "Use the phone here",
  mic: "Set up your microphone",
};

/**
 * The rep's ACD state, or null for somebody the ACD has nothing to do with: a phone that staffs no
 * line and is not signed in to one, which is every admin and most supervisors.
 *
 * Only asked of the API for a signed-in account with a phone. The gallery at `/dev/phone` has no
 * account, and seeds the cache instead — which a disabled query still reads.
 */
export function useRepAvailability(): { me: AgentState; view: Availability } | null {
  const { user } = useAuth();
  const { data: me } = useAcdMe({ enabled: Boolean(user?.sip_endpoint_id) });
  if (!me) return null;
  const view = availability(me, !seesCampaigns(user?.role));
  if (me.lines.length === 0 && !view.signedIn) return null;
  return { me, view };
}

/**
 * Why a Ready rep gets no dialer calls, campaign by campaign: stopped at its abandon limit
 * (ACL-536), no lead due until a time, a campaign that cannot dial, every cadence paused, or their
 * own callers only (ACL-568). Under the taking-calls control, in the phone and on the Phone page.
 * It goes with the next read of the rep's lines once the dialer gives them a lead, and at once
 * when the lead is an offer.
 */
export function NoCallsLine({ className }: { className?: string }) {
  const { user } = useAuth();
  const current = useRepAvailability();
  const { data: offer } = useDialerOffer({ enabled: Boolean(user?.sip_endpoint_id) });
  const line = current ? noCallsLine(current.me, new Date(), Boolean(offer), !seesCampaigns(user?.role)) : null;
  if (line === null) return null;
  return (
    <Callout tone={line.tone} className={className}>
      {line.text}
    </Callout>
  );
}

/**
 * Taking calls: Ready, My calls only, a break and its reason, and sign out. In the phone's header,
 * because the phone is the one surface that follows a rep across every screen (AGENTS.md).
 *
 * The trigger reads the state the ACD recorded, never the item last picked: a pick goes out as a
 * command, and the label changes when the event-tracker says the ACD applied it (`lib/acd.ts`).
 * Items that do not apply stay in the menu, dimmed, with the reason under them, so "why can I not
 * sign out" is answered where the question is asked.
 *
 * The menu is read by its pictures first: a band in the state's tone with the state, the two ways
 * to take calls as rows with a disc each, the rep's campaigns with a switch each, the breaks as four
 * tiles, and sign out. The choice the rep is on is tinted and ticked, and is not dimmed even where a
 * press would change nothing; the break they are on is the tile with the ring.
 *
 * Each campaign the rep staffs is a switch, as tap-ucaas lists its campaigns to join or leave
 * (ACL-484): on while they are signed in to its line, and a press signs in to or out of that
 * campaign only, at once (`lineSwitches`). The menu stays open, so several can be turned in a row.
 * Ready from signed out still signs in to every one; back from a break it leaves the ones turned
 * off as they are. A rep has no switches and sees no campaign (ACL-652): their Ready signs in to all.
 *
 * Ready reads Ready for every rep. For one with a number of their own on a campaign, the campaign's
 * routing decides whether it takes every call there or only their own callers (ADR-037, amended
 * 2026-09-17), and the phone does not say which.
 *
 * My calls only is the rep's own choice, for one call (ADR-070): Ready, but only for a call to their
 * number or a caller a colleague sends them by name, until they answer one. A rep waiting for a
 * transfer to their number takes it so no other call reaches them first. The ACD ends it, and the
 * phone reads "Ready · My calls only" until then; Ready ends it sooner.
 *
 * `large` is the Phone page's: the first control on the page, with the whole state spelled out
 * and a chevron, where the dock's header has room only for the short word.
 */
export function AvailabilityMenu({ large = false }: { large?: boolean } = {}) {
  const current = useRepAvailability();
  const { user } = useAuth();
  const { data: offer } = useDialerOffer({ enabled: Boolean(user?.sip_endpoint_id) });
  const act = useAgentAction();
  const toast = useToast();
  const phone = useSoftphone();
  const blocked = useTakeCallsBlock();
  if (!current) return null;
  const { me, view } = current;

  const run = (steps: AgentStep[]) => {
    act.mutate(
      steps,
      {
        onError: (e) =>
          toast.error(
            "The ACD did not take that",
            e instanceof ApiError ? e.message : "Please try again.",
          ),
      },
    );
  };
  // A rep has no switches and reads no line's name (ACL-652): Ready signs them in to all of them.
  const sees = seesCampaigns(user?.role);
  const switches = sees ? lineSwitches(me, offer?.campaign_id ?? null) : [];
  const notYetOn = sees && view.notYetOn.length > 0 ? linesText(view.notYetOn) : null;
  const status = notYetOn ? `${view.label}, not yet on ${notYetOn}` : view.label;
  const onBreak = view.state === "paused" ? (me.state?.code ?? null) : null;
  const readyChecked = view.signedIn && view.state === "available" && !view.ownCallsNext;
  const StateIcon =
    (onBreak && BREAK_ICON[onBreak]) || (view.state ? (STATE_ICON[view.state] ?? PhoneCall) : PhoneOff);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Taking calls: ${status}. Change it`}
          className={cn(
            "flex shrink-0 items-center rounded-md border transition-colors",
            large
              ? cn("h-9 gap-2 px-3 text-sm font-semibold shadow-xs", AVAILABILITY_FILL[view.tone])
              : "h-7 gap-1.5 border-border bg-surface px-2 text-xs font-medium text-text hover:bg-surface-hover",
          )}
        >
          {act.isPending ? (
            <Spinner className={large ? "size-3" : "size-2.5"} />
          ) : (
            <span
              className={cn("rounded-full", large ? "size-2.5" : "size-2", AVAILABILITY_DOT[view.tone])}
              aria-hidden
            />
          )}
          {large ? view.label : view.short}
          {large && <ChevronDown className="size-4 opacity-70" aria-hidden />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={large ? "start" : "end"} className="w-72 p-0">
        <div
          className={cn(
            "flex items-start gap-3 border-b border-border px-3 py-3",
            AVAILABILITY_BAND[view.tone],
          )}
        >
          <span
            className={cn("flex size-9 shrink-0 items-center justify-center rounded-full", DISC[view.tone])}
            aria-hidden
          >
            <StateIcon className="size-[1.125rem]" />
          </span>
          <div className="min-w-0 flex-1">
            <DropdownMenuLabel className="p-0">Taking calls</DropdownMenuLabel>
            <p className="text-sm font-semibold text-text">{view.label}</p>
          </div>
        </div>
        {blocked && (
          <div
            className={cn("border-b border-border bg-warning-2 px-3 pt-2.5", blocked.fix ? "pb-1.5" : "pb-2.5")}
          >
            <p className="flex items-start gap-2 text-xs text-warning-11">
              {blocked.fix === "mic" ? (
                <MicOff className="mt-px size-3.5 shrink-0" aria-hidden />
              ) : (
                <PhoneOff className="mt-px size-3.5 shrink-0" aria-hidden />
              )}
              {blocked.text}
            </p>
            {blocked.fix && (
              <DropdownMenuItem
                onSelect={() => {
                  if (blocked.fix === "mic") openAudioSetup();
                  else if (blocked.fix === "take_over") void phone.takeOver();
                  else void phone.connect();
                }}
                className={cn("mt-1.5 gap-2 px-2 py-1.5 font-medium text-accent-11", ITEM_ICONS)}
              >
                {blocked.fix === "mic" ? <Headphones /> : <Phone />}
                {FIX_LABEL[blocked.fix]}
              </DropdownMenuItem>
            )}
          </div>
        )}
        <div className="p-1">
          <MenuAction
            icon={Headset}
            tone="success"
            title="Ready"
            detail={readyChecked || !blocked ? view.ready.detail : blocked.short}
            disabled={blocked !== null || view.ready.steps.length === 0}
            checked={readyChecked}
            onSelect={() => run(view.ready.steps)}
          />
          <MenuAction
            icon={UserCheck}
            tone="accent"
            title="My calls only"
            detail={view.ownCallsNext || !blocked ? view.myCalls.detail : blocked.short}
            disabled={blocked !== null || view.myCalls.steps.length === 0}
            checked={view.ownCallsNext}
            onSelect={() => run(view.myCalls.steps)}
          />
          {switches.length > 0 && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Your campaigns</DropdownMenuLabel>
              {switches.map((line) => (
                <CampaignSwitch
                  key={line.name}
                  // Turning one off is always allowed; turning one on puts calls through.
                  line={blocked && !line.on && !line.locked ? { ...line, locked: blocked.short } : line}
                  pending={act.isPending}
                  onToggle={() => run(line.steps)}
                />
              ))}
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuLabel>Take a break</DropdownMenuLabel>
          <div className="grid grid-cols-4 gap-1 px-1">
            {BREAK_REASONS.map((reason) => {
              const Icon = BREAK_ICON[reason.code] ?? Coffee;
              const current = onBreak === reason.code;
              return (
                <DropdownMenuItem
                  key={reason.code}
                  disabled={view.breakBlocked !== null}
                  onSelect={() => run([{ action: "pause", queue: null, code: reason.code }])}
                  className={cn(
                    "group flex-col gap-1.5 px-0 py-2 text-xs",
                    ITEM_ICONS,
                    current && "font-medium data-[disabled]:opacity-100",
                  )}
                >
                  <span
                    className={cn(
                      "flex size-9 items-center justify-center rounded-full transition-colors",
                      current
                        ? cn(DISC.warning, "ring-2 ring-warning-9 ring-offset-2 ring-offset-surface-raised")
                        : "bg-warning-2 text-warning-11 group-data-[highlighted]:bg-warning-4",
                    )}
                    aria-hidden
                  >
                    <Icon />
                  </span>
                  {reason.label}
                  {current && <span className="sr-only">, the break you are on</span>}
                </DropdownMenuItem>
              );
            })}
          </div>
          {view.breakBlocked && (
            <p className="px-2 pb-1 pt-0.5 text-center text-xs text-text-muted">{view.breakBlocked}</p>
          )}
          <DropdownMenuSeparator />
          <MenuAction
            icon={LogOut}
            tone="danger"
            title="Sign out of your lines"
            detail={view.signOutBlocked ?? "Calls stop coming to you until you are Ready again"}
            disabled={view.signOutBlocked !== null}
            destructive
            onSelect={() => run([{ action: "logout", queue: null }])}
          />
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * A campaign the rep staffs, with its switch (ACL-484): a row of the menu that reads as a checkbox
 * to a screen reader, its name and, while it cannot be turned, why. The switch is drawn, not a
 * control of its own: the row is the control, and a button inside a menu item is two.
 *
 * Pressing it keeps the menu open, so a rep can turn several in a row. It shows what the ACD
 * recorded, never the press, so it moves when the record does.
 */
function CampaignSwitch({
  line, pending, onToggle,
}: {
  line: LineSwitch;
  pending: boolean;
  onToggle: () => void;
}) {
  const disabled = line.locked !== null || pending;
  return (
    <DropdownMenuItem
      role="menuitemcheckbox"
      aria-checked={line.on}
      disabled={disabled}
      onSelect={(event) => {
        event.preventDefault();
        onToggle();
      }}
      className="gap-3 px-2 py-1.5"
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={cn("truncate", line.on ? "font-medium text-text" : "text-text-muted")}>
          {line.name}
        </span>
        {line.locked && <span className="text-xs text-text-muted">{line.locked}</span>}
      </span>
      <span
        className={cn(
          "flex h-4.5 w-8 shrink-0 items-center rounded-full border-2 border-transparent transition-colors",
          line.on ? "bg-success-9" : "bg-gray-6",
        )}
        aria-hidden
      >
        <span
          className={cn(
            "block size-3.5 rounded-full bg-white shadow-sm transition-transform",
            line.on ? "translate-x-3.5" : "translate-x-0",
          )}
        />
      </span>
    </DropdownMenuItem>
  );
}

function MenuAction({
  icon: Icon, tone, title, detail, disabled, destructive = false, checked = false, onSelect,
}: {
  icon: LucideIcon;
  /** The disc's tone, and the row's own when it is the rep's current choice. */
  tone: "success" | "accent" | "danger";
  title: string;
  detail: string;
  disabled: boolean;
  destructive?: boolean;
  /** The choice the rep is on now (ADR-037, ADR-070): tinted and ticked, its detail saying what a
   *  press would do, and never dimmed, since it is the one row that is true. */
  checked?: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      disabled={disabled}
      destructive={destructive}
      onSelect={onSelect}
      className={cn(
        "gap-3 px-2 py-2",
        ITEM_ICONS,
        checked && tone !== "danger" && cn(CURRENT_ROW[tone], "data-[disabled]:opacity-100"),
      )}
    >
      <span
        className={cn("flex size-8 shrink-0 items-center justify-center rounded-lg", DISC[tone])}
        aria-hidden
      >
        <Icon />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="font-medium">{title}</span>
        <span className="text-xs text-text-muted">{detail}</span>
      </span>
      {checked && tone !== "danger" && (
        <span className={AVAILABILITY_TEXT[tone]}>
          <Check aria-label="Current" />
        </span>
      )}
    </DropdownMenuItem>
  );
}
